export const DRIVER_MEDIA_MAX_BYTES = 2 * 1024 * 1024;
export const DRIVER_MEDIA_MAX_SOURCE_BYTES = 20 * 1024 * 1024;
export const DRIVER_MEDIA_MAX_DIMENSION = 1920;

const MIME_SIGNATURES = [
  { mime: "image/jpeg", test: (bytes) => bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff },
  { mime: "image/png", test: (bytes) => bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47 },
  { mime: "image/webp", test: (bytes) => String.fromCharCode(...bytes.slice(0, 4)) === "RIFF" && String.fromCharCode(...bytes.slice(8, 12)) === "WEBP" },
];
const ALLOWED_MIME = new Set(MIME_SIGNATURES.map((item) => item.mime));

export async function detectDriverImageMime(file) {
  if (!file || typeof file.slice !== "function") return null;
  const bytes = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  return MIME_SIGNATURES.find((signature) => signature.test(bytes))?.mime || null;
}

function imageFromElement(file) {
  return new Promise((resolve, reject) => {
    const sourceUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve({
      source: image,
      width: image.naturalWidth,
      height: image.naturalHeight,
      close: () => URL.revokeObjectURL(sourceUrl),
    });
    image.onerror = () => {
      URL.revokeObjectURL(sourceUrl);
      reject(new Error("Foto tidak dapat dibaca pada perangkat ini."));
    };
    image.src = sourceUrl;
  });
}

async function decodeImage(file) {
  if ("createImageBitmap" in globalThis) {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        close: () => bitmap.close?.(),
      };
    } catch {
      // Android WebView variants can reject createImageBitmap for valid camera images.
    }
  }
  return imageFromElement(file);
}

function canvasBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
}

function safeFileName(file) {
  const stem = String(file?.name || "foto").replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9._-]/g, "-") || "foto";
  return `${stem}.jpg`;
}

export async function normalizeDriverImage(file, {
  maxBytes = DRIVER_MEDIA_MAX_BYTES,
  maxDimension = DRIVER_MEDIA_MAX_DIMENSION,
} = {}) {
  if (!file || typeof file.size !== "number" || file.size <= 0) {
    throw new Error("File foto kosong atau tidak dapat dibaca.");
  }
  if (file.size > DRIVER_MEDIA_MAX_SOURCE_BYTES) {
    throw new Error("Foto terlalu besar. Pilih foto berukuran maksimal 20 MB.");
  }

  const detectedMime = await detectDriverImageMime(file);
  const declaredMime = String(file.type || "").toLowerCase();
  if (!detectedMime || !ALLOWED_MIME.has(detectedMime)) {
    if (/hei[cf]/.test(declaredMime) || /\.hei[cf]$/i.test(file.name || "")) {
      throw new Error("Format HEIC/HEIF belum didukung. Gunakan kamera Android atau pilih foto JPEG, PNG, atau WebP.");
    }
    throw new Error("Format foto harus JPEG, PNG, atau WebP.");
  }

  const decoded = await decodeImage(file);
  try {
    if (!decoded.width || !decoded.height) throw new Error("Ukuran foto tidak dapat dibaca.");
    if (file.size <= maxBytes && Math.max(decoded.width, decoded.height) <= maxDimension) {
      const normalizedFile = declaredMime === detectedMime
        ? file
        : new File([file], file.name || `foto-${Date.now()}`, { type: detectedMime, lastModified: file.lastModified || Date.now() });
      return { file: normalizedFile, width: decoded.width, height: decoded.height, compressed: false };
    }

    let scale = Math.min(1, maxDimension / Math.max(decoded.width, decoded.height));
    let canvas;
    let blob;
    for (let sizeAttempt = 0; sizeAttempt < 3 && (!blob || blob.size > maxBytes); sizeAttempt += 1) {
      canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(decoded.width * scale));
      canvas.height = Math.max(1, Math.round(decoded.height * scale));
      const context = canvas.getContext("2d", { alpha: false });
      if (!context) throw new Error("Perangkat tidak dapat memproses foto.");
      context.drawImage(decoded.source, 0, 0, canvas.width, canvas.height);
      for (const quality of [0.82, 0.72, 0.6, 0.48]) {
        blob = await canvasBlob(canvas, quality);
        if (blob && blob.size <= maxBytes) break;
      }
      scale *= 0.78;
    }
    if (!blob || blob.size > maxBytes) {
      throw new Error("Foto masih lebih besar dari 2 MB setelah diperkecil.");
    }
    return {
      file: new File([blob], safeFileName(file), { type: "image/jpeg", lastModified: Date.now() }),
      width: canvas.width,
      height: canvas.height,
      compressed: true,
    };
  } finally {
    decoded.close();
  }
}
